import type { TopicId } from './types';

export interface Card {
  readonly topicId: TopicId;
  readonly name: string;
  readonly formula: string;
  readonly note: string;
}

/**
 * The reference cards. Available from anywhere, including mid-question.
 *
 * This is deliberate, not a concession: nobody on a shop floor works these
 * from memory, and making her feel she should is how the subject became
 * frightening in the first place. Looking it up is the professional habit.
 */
export const CARDS: readonly Card[] = [
  {
    topicId: 'speeds',
    name: 'Spindle speed',
    formula: 'RPM = (3.82 × SFM) ÷ D',
    note: 'D is the cutter diameter in inches. SFM comes off the tooling chart. 3.82 is 12 ÷ π rounded.',
  },
  {
    topicId: 'speeds',
    name: 'Feed rate',
    formula: 'IPM = RPM × flutes × chip load',
    note: 'Chip load is per tooth, per revolution — not per minute and not per pass.',
  },
  {
    topicId: 'frac',
    name: 'Fraction to decimal',
    formula: 'top ÷ bottom',
    note: 'Sixteenths step by .0625. Eighths by .125. Thirty-seconds by .03125.',
  },
  {
    topicId: 'inmm',
    name: 'Inch and millimetre',
    formula: 'in × 25.4 = mm',
    note: 'Going the other way, divide by 25.4. To millimetres the number always gets bigger.',
  },
  {
    topicId: 'tenths',
    name: 'Thou and tenths',
    formula: '0.001" = one thou · 0.0001" = one tenth',
    note: 'A "tenth" means a tenth of a thou, not a tenth of an inch. On a diameter, take half per side.',
  },
  {
    topicId: 'trig',
    name: 'Right triangle',
    formula: 'sin = opp/hyp · cos = adj/hyp · tan = opp/adj',
    note: 'Name the sides from the angle you are working off, not from how the drawing is turned.',
  },
  {
    topicId: 'trig',
    name: 'Finding an angle',
    formula: 'angle = tan⁻¹(opp ÷ adj)',
    note: 'The inverse key is the shifted one. It turns a ratio back into an angle.',
  },
  {
    topicId: 'bolt',
    name: 'Bolt circle',
    formula: 'angle = 360 ÷ holes · chord = D × sin(180 ÷ n)',
    note: 'X = R × cos(angle), Y = R × sin(angle). The callout is a diameter, so R is half of it.',
  },
  {
    topicId: 'taper',
    name: 'Taper',
    formula: 'TPI = (big Ø − small Ø) ÷ length',
    note: 'TPF = TPI × 12. The compound gets half the included angle, because it only cuts one side.',
  },
  {
    topicId: 'tol',
    name: 'Limits',
    formula: 'max = nominal + upper · min = nominal − lower',
    note: 'Total tolerance is the whole band between the two limits. ± doubles the number on the print.',
  },
  {
    topicId: 'tol',
    name: 'Fits',
    formula: 'allowance = hole − shaft',
    note: 'Positive is clearance, it drops in. Negative is interference, it needs force or heat.',
  },
];
